import { Link, NavLink } from "react-router-dom";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "./ui/button";
import { logout } from "../lib/auth";
import { useAuthStore } from "../stores/auth";
import { cn } from "../lib/cn";

interface NavItem {
  to: string;
  label: string;
  /** Optional i18n key — when present `t(i18nKey)` overrides `label`. */
  i18nKey?: string;
}

const NAV: NavItem[] = [
  { to: "/dashboard", label: "Dashboard" },
  { to: "/templates", label: "Templates" },
  { to: "/imports", label: "Imports" },
  { to: "/brand-kit", label: "Brand kit", i18nKey: "nav.brand_kit" },
  { to: "/api-keys", label: "API keys" },
  { to: "/webhooks", label: "Webhooks" },
  { to: "/team", label: "Team" },
  { to: "/settings", label: "Settings" },
];

/**
 * Authenticated app shell — sidebar + topbar + content area. T2.3 ships
 * the chrome; T2.4+ fill the additional nav targets. Each NavLink applies
 * the active-route style automatically via React Router.
 */
export function PortalShell({ children }: { children: ReactNode }): JSX.Element {
  const user = useAuthStore((state) => state.user);
  const { t } = useTranslation();

  async function onLogout(): Promise<void> {
    await logout();
    window.location.assign("/");
  }

  return (
    <div className="flex min-h-full flex-col bg-neutral-50 lg:flex-row">
      <aside className="border-b border-neutral-200 bg-white p-4 lg:w-60 lg:border-b-0 lg:border-r lg:p-6">
        <Link
          to="/dashboard"
          className="mb-6 hidden text-lg font-semibold text-neutral-900 lg:block"
        >
          Email Builder
        </Link>
        <nav className="flex gap-2 lg:flex-col lg:gap-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  "rounded-md px-3 py-2 text-sm font-medium",
                  isActive
                    ? "bg-primary-50 text-primary-700"
                    : "text-neutral-700 hover:bg-neutral-100",
                )
              }
            >
              {item.i18nKey !== undefined ? t(item.i18nKey, item.label) : item.label}
            </NavLink>
          ))}
        </nav>
      </aside>
      <div className="flex flex-1 flex-col">
        <header className="flex items-center justify-between border-b border-neutral-200 bg-white px-6 py-3">
          <p className="text-sm text-neutral-600">
            {user?.email ?? "Signed in"}
          </p>
          <Button variant="outline" size="sm" onClick={() => void onLogout()}>
            Sign out
          </Button>
        </header>
        <main className="flex-1 px-6 py-8">{children}</main>
      </div>
    </div>
  );
}
